"use client";

import { IconLock, IconStarFilled } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type ProgressCustomProps = {
  value: number;
  tiers: {
    fromAmount: number;
    fromAmountLabel: string;
    valueLabel: string;
  }[];
};

type ProgressLevelItem = {
  fromAmount: number;
  isLastTier: boolean;
  position: number;
  fromAmountLabel: string;
  valueLabel: string;
};

type PositionedProgressLevel = {
  centerPx: number;
  defaultLeftPx: number;
  fromAmount: number;
};

const PROGRESS_LEVEL_WIDTH = 48;
const PROGRESS_LEVEL_GAP = 8;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const getProgressPosition = (fromAmount: number, maxTierFromAmount: number) => {
  if (maxTierFromAmount <= 0) {
    return 0;
  }

  return (fromAmount * 100) / maxTierFromAmount;
};

const getProgressLevelLayout = (
  levels: ProgressLevelItem[],
  trackWidth: number,
) => {
  const positions = new Map<number, number>();

  if (trackWidth <= 0) {
    return positions;
  }

  const maxLeft = Math.max(trackWidth - PROGRESS_LEVEL_WIDTH, 0);
  const preparedLevels = levels
    .map((level) => {
      const centerPx = (level.position / 100) * trackWidth;

      return {
        centerPx,
        defaultLeftPx: clamp(centerPx - PROGRESS_LEVEL_WIDTH / 2, 0, maxLeft),
        fromAmount: level.fromAmount,
      };
    })
    .sort((leftLevel, rightLevel) => leftLevel.centerPx - rightLevel.centerPx);

  const commitGroup = (group: PositionedProgressLevel[]) => {
    if (group.length === 0) {
      return;
    }

    if (group.length === 1) {
      const firstLevel = group.at(0);

      if (!firstLevel) {
        return;
      }

      positions.set(firstLevel.fromAmount, firstLevel.defaultLeftPx);
      return;
    }

    const groupWidth =
      group.length * PROGRESS_LEVEL_WIDTH +
      (group.length - 1) * PROGRESS_LEVEL_GAP;

    const groupCenter =
      group.reduce((total, level) => total + level.centerPx, 0) / group.length;

    const groupLeft = clamp(
      groupCenter - groupWidth / 2,
      0,
      Math.max(trackWidth - groupWidth, 0),
    );

    for (const [index, level] of group.entries()) {
      positions.set(
        level.fromAmount,
        groupLeft + index * (PROGRESS_LEVEL_WIDTH + PROGRESS_LEVEL_GAP),
      );
    }
  };

  let currentGroup: PositionedProgressLevel[] = [];
  let currentGroupRight = Number.NEGATIVE_INFINITY;

  for (const level of preparedLevels) {
    if (
      currentGroup.length > 0 &&
      level.defaultLeftPx > currentGroupRight + PROGRESS_LEVEL_GAP
    ) {
      commitGroup(currentGroup);
      currentGroup = [];
      currentGroupRight = Number.NEGATIVE_INFINITY;
    }

    currentGroup.push(level);
    currentGroupRight = Math.max(
      currentGroupRight,
      level.defaultLeftPx + PROGRESS_LEVEL_WIDTH,
    );
  }

  commitGroup(currentGroup);

  return positions;
};

export const ProgressCustom = ({ value, tiers }: ProgressCustomProps) => {
  const maxTierFromAmount = tiers.at(-1)?.fromAmount ?? 0;
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [trackWidth, setTrackWidth] = useState(0);

  useEffect(() => {
    const trackElement = trackRef.current;

    if (!trackElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      const nextTrackWidth = entry?.contentRect.width ?? 0;

      setTrackWidth((currentTrackWidth) =>
        currentTrackWidth === nextTrackWidth
          ? currentTrackWidth
          : nextTrackWidth,
      );
    });

    resizeObserver.observe(trackElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const levels = useMemo(
    () =>
      tiers
        .map((tier, index) => ({
          fromAmount: tier.fromAmount,
          isLastTier: index === tiers.length - 1,
          position: getProgressPosition(tier.fromAmount, maxTierFromAmount),
          fromAmountLabel: tier.fromAmountLabel,
          valueLabel: tier.valueLabel,
        }))
        .filter(
          (tier) => tier.fromAmount !== 0 && tier.position > value,
        ) satisfies ProgressLevelItem[],
    [maxTierFromAmount, tiers, value],
  );

  const levelLayout = useMemo(
    () => getProgressLevelLayout(levels, trackWidth),
    [levels, trackWidth],
  );

  return (
    <div className="h-22 pt-6">
      <div ref={trackRef} className="relative h-1.5 w-full bg-gray-200">
        <ProgressBar value={value} />

        {levels.map((tier) => (
          <ProgressLevel
            key={tier.fromAmount}
            icon={
              tier.isLastTier ? (
                <IconStarFilled className="h-4 w-4 fill-brand-yellow" />
              ) : (
                <IconLock className="h-4 w-4" />
              )
            }
            leftPx={levelLayout.get(tier.fromAmount)}
            position={tier.position}
            fromAmountLabel={tier.fromAmountLabel}
            valueLabel={tier.valueLabel}
          />
        ))}
      </div>
    </div>
  );
};

const ProgressLevel = ({
  icon,
  position,
  leftPx,
  fromAmountLabel,
  valueLabel,
}: {
  icon: React.ReactNode;
  position: number;
  leftPx?: number;
  fromAmountLabel: string;
  valueLabel: string;
}) => {
  return (
    <div
      style={leftPx === undefined ? { left: `${position}%` } : { left: leftPx }}
      className={cn(
        "absolute top-0 -translate-y-6",
        leftPx === undefined && "-translate-x-1/2",
        leftPx === undefined && position > 90 && "-translate-x-full",
        leftPx === undefined && position < 10 && "translate-x-0",
      )}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full border-4 border-grey-200 bg-white">
        {icon}
      </div>
      <p className="w-12 text-nowrap text-center font-medium text-sm">
        - {valueLabel}
      </p>
      <p className="w-12 whitespace-nowrap text-nowrap text-center text-xs">
        od {fromAmountLabel}
      </p>
    </div>
  );
};

const ProgressIndicator = () => {
  return (
    <div className="absolute top-1/2 right-0 h-3 w-3 -translate-y-1/2 rounded-full bg-brand-orange-dark"></div>
  );
};

const ProgressBar = ({ value }: { value: number }) => {
  return (
    <div
      style={{ width: `${value}%` }}
      className="relative h-full bg-brand-orange"
    >
      <ProgressIndicator />
    </div>
  );
};
